import { Component, ElementRef, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatSort, MatPaginator } from '@angular/material';
import { ObservableMedia, MediaChange } from '@angular/flex-layout';

import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { debounceTime } from 'rxjs/operators/debounceTime';
import { distinctUntilChanged } from 'rxjs/operators/distinctUntilChanged';
import { first } from 'rxjs/operators/first';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import { takeUntil } from 'rxjs/operators/takeUntil';
import { combineLatest } from 'rxjs/observable/combineLatest';
import { fromEvent } from 'rxjs/observable/fromEvent';

import { DataTableSource } from '../../classes/DataTableSource/datatablesource';

import { ApplicationService, CollegeInfo, EligibleCourseInfo } from '../../services/application/application.service';
import { DialogService } from '../../services/dialogs/dialogs.service';
import { DownloadService, DownloadFormat, HttpMethod } from '../../services/download/download.service';
import { ErrorService } from '../../services/error/error.service';
import { SplashService } from '../../services/splash/splash.service';
import { UserInfo, UserService } from '../../services/user/user.service';

import { Tag, TagTypes } from '../ui/tags/tags.component';


@Component({
    selector: 'app-manage-courses',
    styleUrls: ['managecourses.component.css', 'managecourses.component.scss'],
    templateUrl: 'managecourses.component.html',
})
export class ManageCoursesComponent implements OnInit, OnDestroy {
    @ViewChild('paginator') paginator: MatPaginator;
    @ViewChild(MatSort) sorter: MatSort;

    private _subrelease: Subject<void> = new Subject<void>();

    AdminCollege: CollegeInfo;
    AdminCollegeID: number;
    AllHeaders: string[] = ['Prefix', 'Code', 'Colleges', 'Actions'];
    Colleges: CollegeInfo[];
    CollegeAbbreviations: string[];
    CollegeNames: string[];
    CourseEntryForm: FormGroup;
    Courses: EligibleCourseInfo[];
    CoursesData: DataTableSource<EligibleCourseInfo>;
    FilterText: string = "";
    FilteredCourses: EligibleCourseInfo[] = [];
    NoData: boolean;
    PartialViewReady: boolean;
    ShowingCourseEntryPanel: boolean;
    SmallDisplay: boolean;
    TagOptions: Tag[] | null;
    User: UserInfo;
    VisibleHeaders: string[];
    VisibleColumns: string[];
    TagTypes = TagTypes;

    get CollegesCtrl() {
        return this.CourseEntryForm && this.CourseEntryForm.get('Colleges');
    }

    constructor(
        private appService: ApplicationService,
        private dialogService: DialogService,
        private downloadService: DownloadService,
        private errorService: ErrorService,
        private fb: FormBuilder,
        private mediaChange: ObservableMedia,
        private splashService: SplashService,
        private userService: UserService
    ) {}

    ngOnInit() {
        const self = this;

        self.AdminCollege = null;
        self.Colleges = null;
        self.CollegeNames = [];
        self.FilterText = "";
        self.NoData = false;
        self.PartialViewReady = false;
        self.ShowingCourseEntryPanel = false;
        self.SmallDisplay = false;
        self.Courses = null;
        self.CoursesData = new DataTableSource<EligibleCourseInfo>([], self.paginator, self.sorter, self.SortCourses.bind(self), [
            'Prefix',
            'Code',
            'Colleges',
        ]);
        self.TagOptions = null;
        self.User = null;

        self.SmallDisplay = self.mediaChange.isActive('xs') || self.mediaChange.isActive('sm');

        self.mediaChange
            .asObservable()
            .pipe(takeUntil(self._subrelease))
            .subscribe(media => {
                self.SmallDisplay = media.mqAlias === 'xs' || media.mqAlias === 'sm';
                self.VisibleHeaders = self.SmallDisplay ? ['SmallDisplay'] : self.AllHeaders.slice();
                self.VisibleColumns = self.NoData ? ['NoData'] : self.VisibleHeaders.slice();
            });

        // Setup the form group
        self.CourseEntryForm = self.fb.group({
            Prefix: ['', Validators.required],
            Code: ['', Validators.required],
            Colleges: [[] as number[], [Validators.min(1)]],
            EligibleCourseID: [null],
        });

        // fromEvent(self.filter.nativeElement, 'keyup')
        //     .pipe(
        //         takeUntil(self._subrelease),
        //         debounceTime(150),
        //         distinctUntilChanged()
        //     )
        //     .subscribe(() => {
        //         if (!self.CoursesData) {
        //             return;
        //         }
        //         self.CoursesData.filter = this.filter.nativeElement.value;
        //     });

        // Get the first non-null UserInfo object so we can begin setting up the component
        self.userService.UserSubject.pipe(
            takeUntil(self._subrelease),
            first(user => user && !!user.AuthToken)
        ).subscribe(user => {
            self.User = user;

            // Set up constant monitoring of the user object, to catch AuthToken updates.
            self.userService.UserSubject.pipe(takeUntil(self._subrelease)).subscribe(_user => {
                self.User = _user;
            });

            // Load static data for use in the compoment.
            const coursesObs = self.appService.GetAllEligibleCourses(self.User.AuthToken);
            const collegeObs = self.appService.GetAllColleges(self.User.AuthToken);
            const adminCollegeObs = self.appService.SelectedAdminCollegeIDSubject;

            combineLatest(coursesObs, collegeObs, adminCollegeObs, function(courses, colleges, adminCollegeID) {
                return {
                    EligibleCourses: courses,
                    AdminCollegeID: adminCollegeID,
                    Colleges: colleges,
                };
            })
                .pipe(takeUntil(self._subrelease))
                .subscribe(
                    packet => {
                        self.Colleges = packet.Colleges;
                        self.AdminCollegeID = packet.AdminCollegeID;
                        self.Courses = packet.EligibleCourses;
                        self.AdminCollege = null;

                        if (self.AdminCollegeID != null && self.Colleges) {
                            self.AdminCollege = self.Colleges.find(item => {
                                return item.CollegeID === self.AdminCollegeID;
                            });
                        }

                        if (!self.Courses || !self.Courses.length) {
                            // self.NoData = true;

                            // Put in a fake record to trip the display message
                            self.CoursesData.Update([{}]);
                        } else {
                            // self.NoData = false;
                            self.CoursesData.Update(self.Courses);
                        }
                        self.TagOptions = self.Colleges.map((c: CollegeInfo) => new Tag(c.CollegeAbbreviation, c.CollegeID, c.Active));
                        self.CollegeAbbreviations = self.Colleges.map((c: CollegeInfo) => c.CollegeAbbreviation);
                        self.CollegeNames = self.Colleges.map((c: CollegeInfo) => c.CollegeName);

                        self.VisibleHeaders = self.SmallDisplay ? ['SmallDisplay'] : self.AllHeaders.slice();
                        self.VisibleColumns = self.NoData ? ['NoData'] : self.VisibleHeaders.slice();

                        // Ready for component use.
                        self.PartialViewReady = true;
                    },
                    error => {
                        self.errorService.HandleServiceError(error);
                    }
                );
        });
    }

    ngOnDestroy() {
        this._subrelease.next();
        this._subrelease.complete();
    }

    // #region Add Course Cancel Handler Method

    AddCourseCancelHandler() {
        const self = this;

        // Null out the form, then close the panel.
        self.CourseEntryForm.reset();

        self.ShowingCourseEntryPanel = false;
    }

    // #endregion

    // #region Add Eligible Course Method

    AddEligibleCourse(colleges: Tag[]) {
        const self = this;

        // Patch value from child component before continueing
        if (self.CourseEntryForm.get('Colleges'))
        {
            self.CourseEntryForm.get('Colleges')!.patchValue(colleges.map(x => x.id));
        }

        const form = self.CourseEntryForm.getRawValue();
        if (!form) {
            return;
        }

        const nPrefix = (<string>form.Prefix).toUpperCase();
        const nCode = (<string>form.Code).toUpperCase();
        const nColleges = (<number[]>form.Colleges);

        const collegeNames = self.Colleges.filter(x => nColleges.includes(x.CollegeID)).map(x => x.CollegeName).join(', ');

        console.log({colleges, form, collegeNames});

        // Get a confirmation
        self.dialogService
            .OpenConfirmationDialog({
                TitleText: 'Confirmation Requested',
                MessageText:
                    `You are about to add the following new eligible course to DELTA:<br /><br />
            <strong>Prefix: </strong>${nPrefix}<br />
            <strong>Code: </strong>${nCode}<br /><br />
            <strong>Colleges: </strong>${collegeNames}<br /><br />
            Once added, this course will become searchable and usable immediately within DELTA, assuming this ` +
                    `course is provided by a specific college or university. Are you sure you wish to continue?`,
                OKText: 'Yes - Add Course',
                CancelText: 'No - CANCEL',
            })
            .subscribe(result => {
                if (result === true) {
                    // User has opted to proceed. Signal the system to add the course to the system.
                    self.splashService
                        .DisplaySplash({ TitleText: 'Please wait', MessageText: 'Adding course...' })
                        .pipe(takeUntil(self._subrelease))
                        .subscribe(() => {
                            self.appService.AddEligibleCourse(self.User.AuthToken, nPrefix, nCode, nColleges).subscribe(
                                courses => {
                                    // Toast the success
                                    self.splashService.CloseSplash();
                                    self.splashService.SnackbarSuccess('New eligible course added successfully.');

                                    // Update the list of eligible courses
                                    self.UpdateCoursesDisplay(courses);

                                    // Reset the course form
                                    self.CourseEntryForm.reset();

                                    // Hide the panel
                                    self.ShowingCourseEntryPanel = false;
                                },
                                error => {
                                    self.errorService.HandleServiceError(error);
                                }
                            );
                        });
                }
            });
    }

    // #endregion

    // #region Add Eligible Course College Method

    AddEligibleCourseCollege(collegeID: number, eligibleCourseID: number) {
        const self = this;

        if (!eligibleCourseID) {
            return;
        }

        self.appService.AddEligibleCourseCollege(self.User.AuthToken, eligibleCourseID, collegeID).subscribe(
            res => {},
            error => self.errorService.HandleServiceError(error)
        );        
    }

    // #endregion

    // #region Download Eligible Courses PDF Method

    DownloadEligibleCoursesPDF() {
        const self = this;

        // Set splash
        self.splashService
            .DisplaySplash({ TitleText: 'Please wait', MessageText: 'Downloading Eligible Courses PDF...' })
            .pipe(takeUntil(self._subrelease))
            .subscribe(() => {
                self.downloadService
                    .RetrieveDownload(
                        HttpMethod.GET,
                        `Api/Services/PDF/PDFDownloadEligibleCoursesList`,
                        {
                            AuthToken: self.User.AuthToken,
                            Attachment: true,
                        },
                        DownloadFormat.PDF
                    )
                    .pipe(takeUntil(self._subrelease))
                    .subscribe(
                        success => {
                            // Drop the splash?
                            self.splashService.CloseSplash();
                        },
                        error => {
                            self.errorService.HandleServiceError(error);
                        }
                    );
            });
    }

    // #endregion

    // #region Remove Eligible Course Method

    RemoveEligibleCourse(ecor: EligibleCourseInfo) {
        const self = this;

        if (!ecor) {
            return;
        }

        // Get a confirmation
        self.dialogService
            .OpenConfirmationDialog({
                TitleText: 'Confirmation Requested',
                MessageText:
                    `You have chosen to remove the following eligible course from the system:<br /><br />
            <strong>Prefix: </strong>${ecor.Prefix}<br />
            <strong>Code: </strong>${ecor.Code}<br /><br />
            Once removed, this course will no longer be recognized as an eligible dual enrollment course. This ` +
                    `will prevent students and counselors from being able to search for this course and adding it to ` +
                    `course requests. This will <em>not</em> remove this course from any current or previous course ` +
                    `request lists, however. Are you sure you wish to continue?`,
                OKText: 'Yes - Remove Course',
                CancelText: 'No - CANCEL',
            })
            .subscribe(result => {
                if (result === true) {
                    // User has opted to proceed. Signal the system to remove this term window from the college.
                    self.appService.RemoveEligibleCourse(self.User.AuthToken, ecor.EligibleCourseID).subscribe(
                        courses => {
                            // Toast the success
                            self.splashService.CloseSplash();
                            self.splashService.SnackbarSuccess('Eligible course was removed successfully.');

                            // Update the list of eligible courses
                            self.UpdateCoursesDisplay(courses);
                        },
                        error => {
                            self.errorService.HandleServiceError(error);
                        }
                    );
                }
            });
    }

    // #endregion

    // #region Remove Eligible Course College Method

    RemoveEligibleCourseCollege(collegeID: number, eligibleCourseID: number) {
        const self = this;

        if (!eligibleCourseID) {
            return;
        }

        self.appService.RemoveEligibleCourseCollege(this.User.AuthToken, eligibleCourseID, collegeID).subscribe(
            res => {},
            error => self.errorService.HandleServiceError(error)
        );
    }

    // #endregion

    // #region Show Course Entry Editor Panel Method

    ShowCourseEntryEditorPanel(ecor?: EligibleCourseInfo) {
        const self = this;

        // Set up the formgroup to prepare for editing. If we were provided a course, use those values to prefill the formgroup.
        let newform: any;

        if (ecor) {
            newform = {
                Prefix: ecor.Prefix,
                Code: ecor.Code,
                EligibleCourseID: ecor.EligibleCourseID,
            };
        } else {
            // Clear the form
            newform = {
                Prefix: '',
                Code: '',
                EligibleCourseID: null,
            };
        }

        self.CourseEntryForm.patchValue(newform);
        self.CourseEntryForm.updateValueAndValidity({ emitEvent: false });

        self.ShowingCourseEntryPanel = true;
    }

    // #endregion

    // #region Sort Courses Method

    SortCourses(First: EligibleCourseInfo, Second: EligibleCourseInfo) {
        const self = this;

        let PropA: string | number;
        let PropB: string | number;

        switch (self.sorter.active) {
            case 'Prefix':
                [PropA, PropB] = [First.Prefix.toUpperCase(), Second.Prefix.toUpperCase()];
                break;
            case 'Code':
                [PropA, PropB] = [First.Code.toUpperCase(), Second.Code.toUpperCase()];
                break;
        }

        const ValueA = PropA != null && PropA !== '' ? (isNaN(+PropA) ? PropA : +PropA) : PropA;
        const ValueB = PropB != null && PropB !== '' ? (isNaN(+PropB) ? PropB : +PropB) : PropB;

        return (ValueA < ValueB ? -1 : 1) * (self.sorter.direction === 'asc' ? 1 : -1);
    }

    // #endregion

    // #region Update Courses Display Method

    UpdateCoursesDisplay(courses?: EligibleCourseInfo[]) {
        const self = this;

        self.Courses = courses;

        if (!self.Courses || !self.Courses.length) {
            // self.NoData = true;

            // Put in a fake record to trip the display message
            self.CoursesData.Update([{}]);
        } else {
            // self.NoData = false;
            self.CoursesData.Update(self.Courses);
        }
    }

    // #endregion

    // #region Update Filter

    UpdateFilter() {
        const self = this;

        const ltext = self.FilterText.toLocaleLowerCase();
        const collegeAbbreviations = self.CollegeAbbreviations;
        const collegeName = self.CollegeNames;

        self.FilteredCourses = (self.Courses || []).filter((t) => {
            return (
                (!ltext ||
                    !!(t.Colleges || []).some(x => 
                        (collegeAbbreviations.includes(x.CollegeAbbreviation) && x.CollegeAbbreviation.toLocaleLowerCase().indexOf(ltext) >= 0) ||
                        (collegeName.includes(x.CollegeName) && x.CollegeName.toLocaleLowerCase().indexOf(ltext) >= 0)
                    ) ||
                    t.Code.toLocaleLowerCase().indexOf(ltext) >= 0 ||
                    t.Prefix.toLocaleLowerCase().indexOf(ltext) >= 0)
            );
        });
        self.CoursesData.dataChange.next(self.FilteredCourses);
    }

    // #endregion
}



// WEBPACK FOOTER //
// ./src/app/managecourses/managecourses.component.ts